import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import {
  CheckCircle,
  ArrowRight,
  Menu,
  X,
  Building2,
  FileText,
  Users,
  BarChart3,
  CreditCard,
  Shield,
  Zap,
  Clock,
  Star,
  ChevronRight,
  Receipt,
  Briefcase,
  Target,
  TrendingUp,
  Phone,
  Mail,
  MapPin,
} from "lucide-react";

const LandingPage = () => {
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const features = [
    {
      icon: Users,
      title: "Gestión de Clientes",
      description: "Administra tu cartera de clientes con información completa, historial y seguimiento de oportunidades.",
      color: "text-blue-600",
      bg: "bg-blue-100",
    },
    {
      icon: FileText,
      title: "Cotizaciones Profesionales",
      description: "Crea cotizaciones atractivas en segundos con tu marca, términos y condiciones personalizados.",
      color: "text-green-600",
      bg: "bg-green-100",
    },
    {
      icon: Receipt,
      title: "Facturación CFDI 4.0",
      description: "Genera facturas electrónicas válidas ante el SAT con timbrado automático e ilimitado.",
      color: "text-purple-600",
      bg: "bg-purple-100",
    },
    {
      icon: Briefcase,
      title: "Control de Proyectos",
      description: "Organiza tus proyectos, asigna tareas y da seguimiento al avance de cada uno.",
      color: "text-orange-600",
      bg: "bg-orange-100",
    },
    {
      icon: BarChart3,
      title: "Reportes y KPIs",
      description: "Visualiza el rendimiento de tu negocio con dashboards y reportes en tiempo real.",
      color: "text-cyan-600",
      bg: "bg-cyan-100",
    },
    {
      icon: Target,
      title: "Órdenes de Compra",
      description: "Gestiona tus compras a proveedores con control de entregas y pagos pendientes.",
      color: "text-pink-600",
      bg: "bg-pink-100",
    },
  ];

  const plans = [
    {
      id: "base",
      name: "Plan Sin Facturación",
      price: 2500,
      description: "Gestión empresarial completa para tu negocio",
      features: [
        "Gestión de clientes ilimitados",
        "Cotizaciones profesionales",
        "Control de proyectos",
        "Órdenes de compra",
        "Hasta 5 usuarios",
        "Reportes básicos",
        "Soporte por email",
      ],
      cta: "Comenzar prueba gratis",
      popular: false,
    },
    {
      id: "with_billing",
      name: "Plan Con Facturación",
      price: 3000,
      description: "Todo incluido + Facturación Electrónica CFDI 4.0",
      features: [
        "Todo del plan anterior",
        "Facturación Electrónica CFDI 4.0",
        "Timbrado ilimitado",
        "Notas de crédito",
        "Complementos de pago",
        "Cancelación de facturas",
        "Soporte prioritario",
      ],
      cta: "Comenzar prueba gratis",
      popular: true,
    },
  ];

  const testimonials = [
    {
      name: "Carlos Rodríguez",
      company: "Constructora del Valle",
      text: "CIA Servicios® nos ayudó a organizar todos nuestros proyectos y facturación. Ahora tenemos todo en un solo lugar.",
      rating: 5,
    },
    {
      name: "María González",
      company: "Servicios Industriales MG",
      text: "La facturación electrónica es súper fácil. Ya no tenemos que preocuparnos por el timbrado.",
      rating: 5,
    },
    {
      name: "Roberto Hernández",
      company: "Mantenimiento Express",
      text: "El control de clientes y cotizaciones nos ha ahorrado mucho tiempo. Muy recomendable.",
      rating: 5,
    },
  ];

  return (
    <div className="min-h-screen bg-white">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-white/80 backdrop-blur-md border-b">
        <div className="container mx-auto px-4">
          <div className="flex items-center justify-between h-16">
            {/* Logo */}
            <Link to="/" className="flex items-center gap-2">
              <img 
                src="https://customer-assets.emergentagent.com/job_cia-operacional/artifacts/0bkwa552_Logo%20CIA.jpg"
                alt="CIA Servicios®"
                className="h-10 w-auto"
              />
              <span className="text-xl font-bold text-slate-800">CIA Servicios®</span>
            </Link>

            {/* Desktop Menu */}
            <div className="hidden md:flex items-center gap-8">
              <a href="#features" className="text-slate-600 hover:text-slate-900 transition">
                Características
              </a>
              <a href="#pricing" className="text-slate-600 hover:text-slate-900 transition">
                Precios
              </a>
              <a href="#testimonials" className="text-slate-600 hover:text-slate-900 transition">
                Testimonios
              </a>
              <a href="#contact" className="text-slate-600 hover:text-slate-900 transition">
                Contacto
              </a>
            </div>

            {/* CTA Buttons */}
            <div className="hidden md:flex items-center gap-3">
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                className="text-slate-600"
                data-testid="landing-login-btn"
              >
                Iniciar sesión
              </Button>
              <Button
                onClick={() => navigate("/registro")}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
              >
                Prueba gratis
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>

            {/* Mobile Menu Button */}
            <button
              className="md:hidden p-2"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="md:hidden py-4 border-t">
              <div className="flex flex-col gap-4">
                <a href="#features" className="text-slate-600 hover:text-slate-900">
                  Características
                </a>
                <a href="#pricing" className="text-slate-600 hover:text-slate-900">
                  Precios
                </a>
                <a href="#testimonials" className="text-slate-600 hover:text-slate-900">
                  Testimonios
                </a>
                <a href="#contact" className="text-slate-600 hover:text-slate-900">
                  Contacto
                </a>
                <hr />
                <Button
                  variant="outline"
                  onClick={() => navigate("/login")}
                  className="w-full"
                  data-testid="landing-mobile-login-btn"
                >
                  Iniciar sesión
                </Button>
                <Button
                  onClick={() => navigate("/registro")}
                  className="w-full bg-gradient-to-r from-blue-600 to-indigo-600"
                >
                  Prueba gratis
                </Button>
              </div>
            </div>
          )}
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            {/* Badge estilo CIA Factura */}
            <div className="inline-flex items-center gap-2 mb-6">
              <div className="flex items-center gap-2 bg-gradient-to-r from-blue-600 to-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium shadow-lg">
                <div className="w-2 h-2 bg-green-400 rounded-full animate-pulse"></div>
                <span>Sistema Activo</span>
              </div>
              <div className="flex items-center gap-1 bg-white/80 backdrop-blur border border-blue-200 text-blue-700 px-3 py-2 rounded-full text-sm font-medium">
                <Zap className="h-4 w-4" />
                <span>CFDI 4.0 Incluido</span>
              </div>
            </div>
            
            <h1 className="text-4xl md:text-6xl font-bold text-slate-900 mb-6 leading-tight">
              Gestiona tu empresa de{" "}
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                servicios
              </span>{" "}
              de forma profesional
            </h1>
            
            <p className="text-xl text-slate-600 mb-8 max-w-2xl mx-auto">
              Clientes, cotizaciones, proyectos y facturación electrónica en una sola plataforma. 
              Simplifica tu operación y haz crecer tu negocio.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <Button
                size="lg"
                onClick={() => navigate("/registro")}
                className="bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-lg px-8"
              >
                Comenzar prueba gratis
                <ArrowRight className="h-5 w-5 ml-2" />
              </Button>
              <Button
                size="lg"
                variant="outline"
                onClick={() => document.getElementById("features").scrollIntoView({ behavior: "smooth" })}
                className="text-lg px-8"
              >
                Ver características
              </Button>
            </div>

            <div className="flex items-center justify-center gap-6 text-sm text-slate-500">
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                7 días gratis
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Sin tarjeta requerida
              </div>
              <div className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-green-500" />
                Cancela cuando quieras
              </div>
            </div>
          </div>

          {/* Dashboard Preview */}
          <div className="mt-16 max-w-5xl mx-auto">
            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl transform rotate-1 scale-[1.02] opacity-20"></div>
              <div className="relative bg-white rounded-2xl shadow-2xl border overflow-hidden">
                <div className="bg-slate-100 px-4 py-3 flex items-center gap-2">
                  <div className="w-3 h-3 rounded-full bg-red-400"></div>
                  <div className="w-3 h-3 rounded-full bg-yellow-400"></div>
                  <div className="w-3 h-3 rounded-full bg-green-400"></div>
                  <div className="flex items-center gap-2 ml-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_cia-operacional/artifacts/0bkwa552_Logo%20CIA.jpg"
                      alt="CIA Servicios®"
                      className="h-5 w-auto"
                    />
                    <span className="text-sm text-slate-500">ciasistem.com/dashboard</span>
                  </div>
                </div>
                <div className="p-6 bg-gradient-to-br from-slate-50 to-slate-100">
                  {/* Stats Cards */}
                  <div className="grid grid-cols-4 gap-4 mb-6">
                    {[
                      { label: "Clientes Activos", value: "124", icon: Users, color: "blue", trend: "+12%" },
                      { label: "Cotizaciones", value: "$845K", icon: FileText, color: "green", trend: "+8%" },
                      { label: "Proyectos", value: "18", icon: Briefcase, color: "purple", trend: "3 nuevos" },
                      { label: "Facturas CFDI", value: "89", icon: Receipt, color: "orange", trend: "+15%" },
                    ].map((stat, i) => (
                      <div key={i} className="bg-white rounded-lg p-4 shadow-sm border">
                        <div className="flex items-center justify-between mb-2">
                          <p className="text-xs text-slate-500">{stat.label}</p>
                          <stat.icon className={`h-4 w-4 text-${stat.color}-500`} />
                        </div>
                        <p className="text-2xl font-bold text-slate-800">{stat.value}</p>
                        <p className="text-xs text-green-600 mt-1">{stat.trend}</p>
                      </div>
                    ))}
                  </div>
                  
                  {/* Charts Row */}
                  <div className="grid grid-cols-3 gap-4 mb-4">
                    {/* Mini Chart 1 - Revenue */}
                    <div className="bg-white rounded-lg p-4 shadow-sm border col-span-2">
                      <div className="flex items-center justify-between mb-3">
                        <h4 className="text-sm font-medium text-slate-700">Ingresos Mensuales</h4>
                        <Badge className="bg-green-100 text-green-700 text-xs">+24% vs año anterior</Badge>
                      </div>
                      <div className="flex items-end gap-2 h-24">
                        {[35, 45, 30, 60, 75, 55, 80, 90, 70, 85, 95, 100].map((h, i) => (
                          <div key={i} className="flex-1 flex flex-col items-center gap-1">
                            <div 
                              className="w-full bg-gradient-to-t from-blue-500 to-blue-400 rounded-t"
                              style={{ height: `${h}%` }}
                            ></div>
                            <span className="text-[8px] text-slate-400">
                              {['E', 'F', 'M', 'A', 'M', 'J', 'J', 'A', 'S', 'O', 'N', 'D'][i]}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Mini Chart 2 - Pipeline */}
                    <div className="bg-white rounded-lg p-4 shadow-sm border">
                      <h4 className="text-sm font-medium text-slate-700 mb-3">Pipeline</h4>
                      <div className="space-y-2">
                        {[
                          { label: "Ganadas", value: 45, color: "bg-green-500", pct: "45%" },
                          { label: "En proceso", value: 32, color: "bg-blue-500", pct: "32%" },
                          { label: "Pendientes", value: 23, color: "bg-amber-500", pct: "23%" },
                        ].map((item, i) => (
                          <div key={i} className="flex items-center gap-2">
                            <div className={`w-2 h-2 rounded-full ${item.color}`}></div>
                            <span className="text-xs text-slate-600 flex-1">{item.label}</span>
                            <span className="text-xs font-medium text-slate-800">{item.pct}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                  
                  {/* Recent Activity */}
                  <div className="bg-white rounded-lg p-4 shadow-sm border">
                    <h4 className="text-sm font-medium text-slate-700 mb-3">Actividad Reciente</h4>
                    <div className="space-y-2">
                      {[
                        { action: "Cotización enviada", client: "Constructora ABC", amount: "$45,000", time: "Hace 5 min" },
                        { action: "Factura timbrada", client: "Servicios XYZ", amount: "$28,500", time: "Hace 15 min" },
                        { action: "Proyecto completado", client: "Industrial MX", amount: "$120,000", time: "Hace 1 hora" },
                      ].map((item, i) => (
                        <div key={i} className="flex items-center justify-between py-2 border-b border-slate-50 last:border-0">
                          <div className="flex items-center gap-3">
                            <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center">
                              <CheckCircle className="h-4 w-4 text-blue-600" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-slate-700">{item.action}</p>
                              <p className="text-xs text-slate-500">{item.client}</p>
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-sm font-medium text-slate-800">{item.amount}</p>
                            <p className="text-xs text-slate-400">{item.time}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Demo Screenshots Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <Badge className="mb-4 bg-indigo-100 text-indigo-700">Demos del Sistema</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Ve cómo funciona en acción
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Conoce las principales funciones que te ayudarán a gestionar tu negocio de manera profesional
            </p>
          </div>

          {/* Demo Cards */}
          <div className="space-y-16">
            {/* Demo 1: Gestión de Clientes */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <Badge className="mb-4 bg-blue-100 text-blue-700">
                  <Users className="h-3 w-3 mr-1" />
                  CRM Integrado
                </Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Gestión completa de clientes
                </h3>
                <p className="text-slate-600 mb-6">
                  Centraliza toda la información de tus clientes: datos fiscales, historial de cotizaciones, 
                  proyectos y facturación en un solo lugar. Programa seguimientos y nunca pierdas una oportunidad.
                </p>
                <ul className="space-y-3">
                  {[
                    "Fichas de cliente con datos fiscales completos",
                    "Historial de interacciones y documentos",
                    "Seguimientos automáticos y recordatorios",
                    "Segmentación y etiquetas personalizadas",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="order-1 md:order-2">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-blue-500 to-indigo-500 rounded-2xl transform rotate-2 opacity-20"></div>
                  <div className="relative bg-white rounded-2xl shadow-xl border p-4 overflow-hidden">
                    <div className="bg-slate-50 rounded-lg p-4">
                      {/* Client List Demo */}
                      <div className="flex items-center justify-between mb-4">
                        <h4 className="font-semibold text-slate-800">Clientes (124)</h4>
                        <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-xs">+ Nuevo</Button>
                      </div>
                      <div className="space-y-3">
                        {[
                          { name: "CONSTRUCTORA DEL VALLE S.A.", rfc: "CDV850101AAA", status: "Activo", projects: 5 },
                          { name: "SERVICIOS INDUSTRIALES MG", rfc: "SIM920515B12", status: "Activo", projects: 3 },
                          { name: "MANTENIMIENTO EXPRESS", rfc: "MEX880720HK9", status: "Prospecto", projects: 0 },
                        ].map((client, i) => (
                          <div key={i} className="bg-white rounded-lg p-3 border flex items-center justify-between">
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-indigo-500 rounded-full flex items-center justify-center text-white font-bold text-sm">
                                {client.name.charAt(0)}
                              </div>
                              <div>
                                <p className="font-medium text-slate-800 text-sm">{client.name}</p>
                                <p className="text-xs text-slate-500">{client.rfc}</p>
                              </div>
                            </div>
                            <div className="text-right">
                              <Badge className={`text-xs ${client.status === 'Activo' ? 'bg-green-100 text-green-700' : 'bg-amber-100 text-amber-700'}`}>
                                {client.status}
                              </Badge>
                              <p className="text-xs text-slate-500 mt-1">{client.projects} proyectos</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Demo 2: Cotizaciones */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div>
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-green-500 to-emerald-500 rounded-2xl transform -rotate-2 opacity-20"></div>
                  <div className="relative bg-white rounded-2xl shadow-xl border p-4 overflow-hidden">
                    <div className="bg-slate-50 rounded-lg p-4">
                      {/* Quote Demo */}
                      <div className="bg-white rounded-lg border p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div>
                            <p className="text-xs text-slate-500">Cotización</p>
                            <p className="font-bold text-lg text-slate-800">COT-2024-0156</p>
                          </div>
                          <Badge className="bg-amber-100 text-amber-700">Pendiente</Badge>
                        </div>
                        <div className="border-t border-b py-3 my-3">
                          <p className="text-sm font-medium text-slate-700">CONSTRUCTORA DEL VALLE S.A.</p>
                          <p className="text-xs text-slate-500">Servicio de mantenimiento industrial</p>
                        </div>
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-slate-600">Mantenimiento preventivo (10 equipos)</span>
                            <span className="font-medium">$25,000</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Revisión de sistemas eléctricos</span>
                            <span className="font-medium">$15,000</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-slate-600">Materiales y refacciones</span>
                            <span className="font-medium">$5,000</span>
                          </div>
                        </div>
                        <div className="border-t mt-3 pt-3">
                          <div className="flex justify-between text-lg font-bold">
                            <span>Total</span>
                            <span className="text-blue-600">$45,000 MXN</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div>
                <Badge className="mb-4 bg-green-100 text-green-700">
                  <FileText className="h-3 w-3 mr-1" />
                  Cotizaciones Profesionales
                </Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Crea cotizaciones en segundos
                </h3>
                <p className="text-slate-600 mb-6">
                  Genera cotizaciones profesionales con tu logotipo, términos y condiciones. 
                  Envíalas por correo y da seguimiento hasta cerrar la venta.
                </p>
                <ul className="space-y-3">
                  {[
                    "Plantillas personalizadas con tu marca",
                    "Catálogo de productos y servicios",
                    "Envío por email con un clic",
                    "Conversión a factura automática",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            {/* Demo 3: Facturación CFDI */}
            <div className="grid md:grid-cols-2 gap-8 items-center">
              <div className="order-2 md:order-1">
                <Badge className="mb-4 bg-purple-100 text-purple-700">
                  <Receipt className="h-3 w-3 mr-1" />
                  Facturación CFDI 4.0
                </Badge>
                <h3 className="text-2xl font-bold text-slate-900 mb-4">
                  Facturación electrónica cumpliendo con el SAT
                </h3>
                <p className="text-slate-600 mb-6">
                  Genera y timbra tus facturas electrónicas CFDI 4.0 de forma automática. 
                  Incluye todos los complementos: pagos, notas de crédito y más.
                </p>
                <ul className="space-y-3">
                  {[
                    "Timbrado ilimitado incluido",
                    "Complementos de pago automáticos",
                    "Notas de crédito y cancelaciones",
                    "Descarga XML y PDF con QR",
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-2 text-slate-700">
                      <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
              <div className="order-1 md:order-2">
                <div className="relative">
                  <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 rounded-2xl transform rotate-2 opacity-20"></div>
                  <div className="relative bg-white rounded-2xl shadow-xl border p-4 overflow-hidden">
                    <div className="bg-slate-50 rounded-lg p-4">
                      {/* Invoice Demo */}
                      <div className="bg-white rounded-lg border p-4">
                        <div className="flex items-center justify-between mb-4">
                          <div className="flex items-center gap-2">
                            <div className="w-10 h-10 bg-gradient-to-br from-purple-500 to-pink-500 rounded-lg flex items-center justify-center">
                              <Receipt className="h-5 w-5 text-white" />
                            </div>
                            <div>
                              <p className="font-bold text-slate-800">CFDI-A-00089</p>
                              <p className="text-xs text-slate-500">15 Dic 2024</p>
                            </div>
                          </div>
                          <Badge className="bg-green-100 text-green-700">Timbrada ✓</Badge>
                        </div>
                        <div className="grid grid-cols-2 gap-4 text-xs mb-4">
                          <div>
                            <p className="text-slate-500">Receptor</p>
                            <p className="font-medium text-slate-800">CONSTRUCTORA DEL VALLE</p>
                            <p className="text-slate-600">CDV850101AAA</p>
                          </div>
                          <div>
                            <p className="text-slate-500">Método de Pago</p>
                            <p className="font-medium text-slate-800">PUE - Pago en una sola exhibición</p>
                            <p className="text-slate-600">Transferencia electrónica</p>
                          </div>
                        </div>
                        <div className="border-t pt-3 flex items-center justify-between">
                          <div>
                            <p className="text-2xl font-bold text-slate-800">$28,500.00</p>
                            <p className="text-xs text-slate-500">IVA incluido</p>
                          </div>
                          <div className="flex gap-2">
                            <Button size="sm" variant="outline" className="text-xs">
                              <FileText className="h-3 w-3 mr-1" />
                              XML
                            </Button>
                            <Button size="sm" variant="outline" className="text-xs">
                              <FileText className="h-3 w-3 mr-1" />
                              PDF
                            </Button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4">Características</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Todo lo que necesitas para tu negocio
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Herramientas diseñadas específicamente para empresas de servicios en México
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {features.map((feature, index) => (
              <Card key={index} className="border-0 shadow-lg hover:shadow-xl transition-shadow">
                <CardHeader>
                  <div className={`w-12 h-12 ${feature.bg} rounded-xl flex items-center justify-center mb-4`}>
                    <feature.icon className={`h-6 w-6 ${feature.color}`} />
                  </div>
                  <CardTitle className="text-xl">{feature.title}</CardTitle>
                </CardHeader>
                <CardContent>
                  <CardDescription className="text-base">{feature.description}</CardDescription>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <Badge className="mb-4 bg-white/10 text-white hover:bg-white/20">
                ¿Por qué CIA Servicios®?
              </Badge>
              <h2 className="text-3xl md:text-4xl font-bold mb-6">
                Diseñado para empresas de servicios en México
              </h2>
              <p className="text-blue-100 text-lg mb-8">
                Entendemos las necesidades específicas de las empresas de servicios: 
                cotizaciones personalizadas, control de proyectos y facturación electrónica 
                que cumple con el SAT.
              </p>
              
              <div className="space-y-4">
                {[
                  { icon: Shield, text: "Cumplimiento total con SAT y CFDI 4.0" },
                  { icon: Clock, text: "Implementación en menos de 1 hora" },
                  { icon: TrendingUp, text: "Incrementa tu productividad un 40%" },
                  { icon: Users, text: "Soporte en español por expertos" },
                ].map((item, i) => (
                  <div key={i} className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-white/10 rounded-lg flex items-center justify-center">
                      <item.icon className="h-5 w-5" />
                    </div>
                    <span className="text-lg">{item.text}</span>
                  </div>
                ))}
              </div>
            </div>
            
            <div className="relative">
              <div className="bg-white/10 backdrop-blur rounded-2xl p-8">
                <div className="space-y-4">
                  <div className="flex items-center gap-4 p-4 bg-white/10 rounded-xl">
                    <div className="w-12 h-12 bg-green-500 rounded-full flex items-center justify-center">
                      <CheckCircle className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-medium">Cotización enviada</p>
                      <p className="text-sm text-blue-200">Cliente: Constructora ABC</p>
                    </div>
                    <span className="ml-auto text-green-400">$45,000</span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white/10 rounded-xl">
                    <div className="w-12 h-12 bg-blue-500 rounded-full flex items-center justify-center">
                      <Receipt className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-medium">Factura timbrada</p>
                      <p className="text-sm text-blue-200">CFDI-A-00123</p>
                    </div>
                    <span className="ml-auto text-blue-400">$28,500</span>
                  </div>
                  <div className="flex items-center gap-4 p-4 bg-white/10 rounded-xl">
                    <div className="w-12 h-12 bg-purple-500 rounded-full flex items-center justify-center">
                      <Briefcase className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <p className="font-medium">Proyecto completado</p>
                      <p className="text-sm text-blue-200">Mantenimiento Industrial</p>
                    </div>
                    <span className="ml-auto text-purple-400">100%</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section id="pricing" className="py-20 bg-slate-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4">Precios</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Planes simples y transparentes
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              Elige el plan que mejor se adapte a tu negocio. Todos incluyen 7 días de prueba gratis.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-4xl mx-auto">
            {plans.map((plan) => (
              <Card
                key={plan.id}
                className={`relative ${
                  plan.popular ? "border-2 border-blue-500 shadow-xl" : "shadow-lg"
                }`}
              >
                {plan.popular && (
                  <Badge className="absolute -top-3 left-1/2 -translate-x-1/2 bg-blue-600">
                    Más Popular
                  </Badge>
                )}
                <CardHeader className="text-center pb-2">
                  <CardTitle className="text-2xl">{plan.name}</CardTitle>
                  <div className="mt-4">
                    <span className="text-5xl font-bold text-slate-900">
                      ${plan.price.toLocaleString()}
                    </span>
                    <span className="text-slate-500">/mes</span>
                  </div>
                  <CardDescription className="mt-2">{plan.description}</CardDescription>
                </CardHeader>
                <CardContent>
                  <ul className="space-y-3 mb-6">
                    {plan.features.map((feature, index) => (
                      <li key={index} className="flex items-center gap-3">
                        <CheckCircle className="h-5 w-5 text-green-500 flex-shrink-0" />
                        <span className="text-slate-700">{feature}</span>
                      </li>
                    ))}
                  </ul>
                  <Button
                    className={`w-full ${
                      plan.popular
                        ? "bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700"
                        : ""
                    }`}
                    variant={plan.popular ? "default" : "outline"}
                    size="lg"
                    onClick={() => navigate("/registro")}
                  >
                    {plan.cta}
                    <ChevronRight className="h-4 w-4 ml-2" />
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>

          <p className="text-center text-slate-500 mt-8">
            Precios en MXN con IVA incluido. Sin contratos a largo plazo. Cancela cuando quieras.
          </p>
        </div>
      </section>

      {/* Testimonials Section */}
      <section id="testimonials" className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <Badge className="mb-4">Testimonios</Badge>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-900 mb-4">
              Lo que dicen nuestros clientes
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <Card key={index} className="shadow-lg">
                <CardContent className="pt-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(testimonial.rating)].map((_, i) => (
                      <Star key={i} className="h-5 w-5 fill-yellow-400 text-yellow-400" />
                    ))}
                  </div>
                  <p className="text-slate-600 mb-4 italic">"{testimonial.text}"</p>
                  <div>
                    <p className="font-semibold text-slate-900">{testimonial.name}</p>
                    <p className="text-sm text-slate-500">{testimonial.company}</p>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-blue-600 to-indigo-600">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-4">
            Comienza tu prueba gratis hoy
          </h2>
          <p className="text-xl text-blue-100 mb-8 max-w-2xl mx-auto">
            Únete a cientos de empresas que ya gestionan su negocio con CIA Servicios®
          </p>
          <Button
            size="lg"
            onClick={() => navigate("/registro")}
            className="bg-white text-blue-600 hover:bg-blue-50 text-lg px-8"
          >
            Comenzar prueba gratis
            <ArrowRight className="h-5 w-5 ml-2" />
          </Button>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-20 bg-slate-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <div>
              <h2 className="text-3xl font-bold mb-4">¿Tienes dudas?</h2>
              <p className="text-slate-300 mb-8">
                Estamos aquí para ayudarte. Contáctanos y con gusto te asesoramos.
              </p>
              
              <div className="space-y-4">
                <a href="mailto:soporte@ciasistem.com" className="flex items-center gap-3 text-slate-300 hover:text-white">
                  <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                    <Mail className="h-5 w-5" />
                  </div>
                  <span>soporte@ciasistem.com</span>
                </a>
                <div className="flex items-center gap-3 text-slate-300">
                  <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                    <MapPin className="h-5 w-5" />
                  </div>
                  <span>Guadalajara, Jalisco, México</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-center">
              <div className="text-center">
                <img 
                  src="https://customer-assets.emergentagent.com/job_cia-operacional/artifacts/0bkwa552_Logo%20CIA.jpg"
                  alt="CIA Servicios®"
                  className="h-24 w-auto mx-auto mb-4"
                />
                <h3 className="text-2xl font-bold mb-2">CIA Servicios®</h3>
                <p className="text-slate-400">Sistema de gestión empresarial</p>
              </div>
            </div>
          </div>

          <hr className="border-slate-800 my-12" />

          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-400 text-sm">
              © 2026 CIA Servicios®. Todos los derechos reservados.
            </p>
            <div className="flex gap-6 text-sm text-slate-400">
              <a href="/terminos" className="hover:text-white">Términos y condiciones</a>
              <a href="/privacidad" className="hover:text-white">Política de privacidad</a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LandingPage;
